import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { Copy, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { supabase } from '../lib/supabaseClient';
import {
  AFTERCARE_CONNECTED_MIN_TALK_SECONDS,
  buildReportText,
  caseLabel,
  DEFAULT_AFTERCARE_STAFF,
  formatCallDate,
  NO_SUMMARY,
  PeriodKey,
  periodFor,
  PERIODS,
  ReportCall,
  reportNumbers,
  shortSummary,
  staffOptions,
} from './aftercareReportMetrics';

/**
 * Aftercare → Report (T-209). Read-only: what Paul presents for a week or month.
 * Never shows a call duration — "connected" is decided server-side from talk time.
 */
export const AftercareReport = () => {
  const notify = useNotify();
  const [periodKey, setPeriodKey] = useState<PeriodKey>('this_week');
  const [staffEmail, setStaffEmail] = useState(DEFAULT_AFTERCARE_STAFF);
  const period = useMemo(() => periodFor(periodKey), [periodKey]);

  const calls = useQuery(['aftercareReportCalls', period.start, period.end], async () => {
    const { data, error } = await supabase.rpc('get_aftercare_report_calls', {
      p_start: period.start,
      p_end: period.end,
      p_min_talk_seconds: AFTERCARE_CONNECTED_MIN_TALK_SECONDS,
    });
    if (error) throw error;
    return (data ?? []) as ReportCall[];
  });

  const emails = useQuery(['aftercareReportEmails', period.start, period.end], async () => {
    const { count, error } = await supabase
      .from('aftercare')
      .select('id', { count: 'exact', head: true })
      .gte('email_sent_at', period.start)
      .lt('email_sent_at', period.end);
    if (error) throw error;
    return count ?? 0;
  });

  const staff = useMemo(() => staffOptions(calls.data ?? []), [calls.data]);
  const staffName = staff.find((s) => s.email === staffEmail)?.name ?? staffEmail;
  const numbers = useMemo(
    () => reportNumbers(calls.data ?? [], staffEmail, emails.data ?? null),
    [calls.data, staffEmail, emails.data],
  );

  const loading = calls.isLoading || emails.isLoading;
  const error = (calls.error || emails.error) as Error | null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildReportText({ staffName, period, numbers }));
      notify('Report copied', { type: 'success' });
    } catch {
      notify('Could not copy to the clipboard', { type: 'error' });
    }
  };

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriodKey(p.key)}
              className={cn(
                'h-8 px-3 text-sm first:rounded-l-md last:rounded-r-md',
                p.key === periodKey ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <select
          aria-label="Staff"
          value={staffEmail}
          onChange={(e) => setStaffEmail(e.target.value)}
          className="h-8 rounded-md border bg-background px-2 text-sm"
        >
          {staff.map((s) => (
            <option key={s.email} value={s.email}>
              {s.name}
            </option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">{period.label}</span>
        <div className="flex-1" />
        <Button size="sm" onClick={() => void copy()} disabled={loading || !!error}>
          <Copy className="mr-2 h-4 w-4" />
          Copy for report
        </Button>
      </div>

      {error ? (
        <p className="text-sm text-destructive">Could not load the report: {error.message}</p>
      ) : loading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat label="Emails sent" value={numbers.emailsSent ?? '—'} />
            <Stat label="Calls made" value={numbers.callsMade} />
            <Stat label="Calls connected" value={numbers.callsConnected} />
          </div>
          {numbers.familyCallsAnswered > 0 && (
            <p className="text-sm text-muted-foreground">
              Plus {numbers.familyCallsAnswered} call{numbers.familyCallsAnswered === 1 ? '' : 's'} from
              families answered.
            </p>
          )}

          <Card>
            <CardContent className="p-0">
              {numbers.connected.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No connected calls in this period.</p>
              ) : (
                <ul className="divide-y">
                  {numbers.connected.map((c) => (
                    <ConversationRow key={c.communication_id} call={c} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground">
            Calls with families on cases whose cremated remains were delivered. Connected = someone picked up and
            talked for at least {AFTERCARE_CONNECTED_MIN_TALK_SECONDS} seconds (voicemails don't count). Emails = aftercare
            emails sent from Paul's address, automatic or sent from the Families tab.
          </p>
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: number | string }) => (
  <Card>
    <CardContent className="p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-3xl font-semibold tabular-nums">{value}</div>
    </CardContent>
  </Card>
);

const ConversationRow = ({ call }: { call: ReportCall }) => {
  const summary = shortSummary(call.summary);
  return (
    <li className="flex gap-4 px-4 py-3 text-sm">
      <div className="w-14 shrink-0 text-muted-foreground">{formatCallDate(call.occurred_at)}</div>
      <div className="w-56 shrink-0">
        <Link to={`/funerals/${call.funeral_id}/show`} className="font-medium hover:underline">
          {call.family_name || 'Family'} · {caseLabel(call)}
        </Link>
        {call.deceased_name && <div className="text-xs text-muted-foreground">for {call.deceased_name}</div>}
        {call.direction === 'inbound' && <div className="text-xs text-muted-foreground">They called</div>}
      </div>
      <div className={cn('flex-1', !summary && 'text-muted-foreground')}>{summary ?? NO_SUMMARY}</div>
    </li>
  );
};

export default AftercareReport;
